import React from "react";

/*
 * Icon — the single source of truth for every icon on the site.
 *
 * ONE base, no exceptions:
 *   24x24 viewBox, fill="none", stroke="currentColor", strokeWidth 1.5,
 *   round caps + joins. Every glyph below is drawn on that base, on a shared
 *   optical grid (~20x20 live area inside the 24 box) so a wide glyph (cloud)
 *   and a narrow one (mobile) read at the same visual weight:
 *     - wide glyphs fill the 20 horizontally and stay short vertically
 *     - tall glyphs fill the 20 vertically and stay narrow horizontally
 *     - square glyphs sit in ~18x18; circles cap at r 9.5 (circles read larger)
 *
 * Usage:  import Icon from "../icons/Icon";  <Icon name="cloud" size={28} />
 * Never inline an <svg> in a section file, and never override strokeWidth.
 */

const BASE = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};

/* The one stroke weight the whole site is drawn at. Exported so anything that
   has to match an icon optically can read it instead of guessing. */
export const ICON_STROKE_WIDTH = BASE.strokeWidth;

const GLYPHS = {
  /* ------------------------------------------------------------------ *
   * capability
   * ------------------------------------------------------------------ */

  /* product — design/product artboard with layout guides */
  product: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M3 9.5h18" />
      <path d="M9.5 21V9.5" />
    </>
  ),

  /* cloud — wide glyph, fills the grid horizontally */
  cloud: (
    <path d="M6.5 18.5C3.9 18.5 2.5 16.9 2.5 15c0-1.9 1.4-3.4 3.3-3.6C6.1 8.3 8.7 6 11.9 6c3 0 5.6 2.1 6.1 5 2.1 0.3 3.5 2 3.5 4s-1.5 3.5-3.9 3.5Z" />
  ),

  /* data — database cylinder, tall glyph */
  data: (
    <>
      <ellipse cx="12" cy="6" rx="7.5" ry="3" />
      <path d="M4.5 6v12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3V6" />
      <path d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3" />
    </>
  ),

  /* ai — a compute core with a spark at its centre */
  ai: (
    <>
      <rect x="5" y="5" width="14" height="14" rx="3.5" />
      <path d="M12 8.4c0 2 1.6 3.6 3.6 3.6-2 0-3.6 1.6-3.6 3.6 0-2-1.6-3.6-3.6-3.6 2 0 3.6-1.6 3.6-3.6Z" />
      <path d="M9.5 2.5V5M14.5 2.5V5M9.5 19v2.5M14.5 19v2.5M2.5 9.5H5M2.5 14.5H5M19 9.5h2.5M19 14.5h2.5" />
    </>
  ),

  /* security — shield with a keyhole (application security) */
  security: (
    <>
      <path d="M12 2.8 4.5 5.9v5.5c0 4.8 3.1 8.9 7.5 10.1 4.4-1.2 7.5-5.3 7.5-10.1V5.9L12 2.8Z" />
      <circle cx="12" cy="11" r="1.8" />
      <path d="M12 12.8v2.4" />
    </>
  ),

  /* automation — a process running on a loop */
  automation: (
    <>
      <path d="M20.5 12a8.5 8.5 0 0 0-8.5-8.5 8.5 8.5 0 0 0-6.4 2.9L3.5 8.4" />
      <path d="M3.5 3.9v4.5H8" />
      <path d="M3.5 12a8.5 8.5 0 0 0 8.5 8.5 8.5 8.5 0 0 0 6.4-2.9l2.1-2" />
      <path d="M20.5 20.1v-4.5H16" />
      <rect x="9.75" y="9.75" width="4.5" height="4.5" rx="1.2" />
    </>
  ),

  /* analytics — plotted bars on an axis */
  analytics: (
    <>
      <path d="M3.5 3.5V19a1.5 1.5 0 0 0 1.5 1.5h15.5" />
      <path d="M8 20.5V16M12.5 20.5v-8M17 20.5V8.5" />
    </>
  ),

  /* mobile — tall glyph, fills the grid vertically */
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2.5" />
      <path d="M10.5 5h3" />
      <path d="M10.75 18.6h2.5" />
    </>
  ),

  /* devops — the continuous loop */
  devops: (
    <>
      <path d="M12 12c1.7-2.6 3.2-4.2 5.4-4.2a4.2 4.2 0 0 1 0 8.4c-2.2 0-3.7-1.6-5.4-4.2Z" />
      <path d="M12 12c-1.7 2.6-3.2 4.2-5.4 4.2a4.2 4.2 0 0 1 0-8.4c2.2 0 3.7 1.6 5.4 4.2Z" />
    </>
  ),

  /* api — a plug meeting a socket: an endpoint you connect to */
  api: (
    <>
      <path d="M9 2.5V7M15 2.5V7" />
      <path d="M6.5 7h11v4.5a5.5 5.5 0 0 1-11 0V7Z" />
      <path d="M12 17v4.5" />
    </>
  ),

  /* quality — QA checklist with a passing tick (never a star) */
  quality: (
    <>
      <path d="M9 4.5H7A1.5 1.5 0 0 0 5.5 6v13.5A1.5 1.5 0 0 0 7 21h10a1.5 1.5 0 0 0 1.5-1.5V6A1.5 1.5 0 0 0 17 4.5h-2" />
      <rect x="9" y="2.5" width="6" height="4" rx="1.25" />
      <path d="m8.75 13 2.25 2.25L15.5 10.5" />
    </>
  ),

  /* integration — two systems wired to each other */
  integration: (
    <>
      <rect x="2.5" y="3" width="8" height="8" rx="2" />
      <rect x="13.5" y="13" width="8" height="8" rx="2" />
      <path d="M10.5 7H15a2.5 2.5 0 0 1 2.5 2.5V13" />
      <path d="M13.5 17H9a2.5 2.5 0 0 1-2.5-2.5V11" />
    </>
  ),

  /* architecture — a system decomposed into services */
  architecture: (
    <>
      <rect x="9" y="2.5" width="6" height="5" rx="1.5" />
      <rect x="2.5" y="16.5" width="6" height="5" rx="1.5" />
      <rect x="15.5" y="16.5" width="6" height="5" rx="1.5" />
      <path d="M12 7.5V12" />
      <path d="M5.5 16.5V12h13v4.5" />
    </>
  ),

  /* support — a headset on the other end of the line */
  support: (
    <>
      <path d="M4 14v-2.5a8 8 0 0 1 16 0V14" />
      <rect x="2.5" y="13.5" width="4" height="6" rx="1.8" />
      <rect x="17.5" y="13.5" width="4" height="6" rx="1.8" />
      <path d="M17.5 19.5v.5a2 2 0 0 1-2 2H13" />
    </>
  ),

  /* ------------------------------------------------------------------ *
   * industry
   * ------------------------------------------------------------------ */

  /* health — heart carrying a vitals trace */
  health: (
    <>
      <path d="M18.9 13.9c1.4-1.4 2.9-3.1 2.9-5.4A5.4 5.4 0 0 0 16.4 3c-1.7 0-2.9.5-4.4 2-1.5-1.5-2.7-2-4.4-2A5.4 5.4 0 0 0 2.2 8.5c0 2.3 1.5 4 2.9 5.4l6.9 6.9Z" />
      <path d="M6 10.6h2.9l1.5-2.4 2 4.4 1.4-2h3.2" />
    </>
  ),

  /* finance — banking hall: pediment roof over columns */
  finance: (
    <>
      <path d="M3 9.2 12 4l9 5.2" />
      <path d="M4.2 10.5h15.6" />
      <path d="M6.8 10.5v8M12 10.5v8M17.2 10.5v8" />
      <path d="M3.5 20.5h17" />
    </>
  ),

  /* retail — shopping bag */
  retail: (
    <>
      <path d="M5.5 8h13l-1.05 12.05a1.6 1.6 0 0 1-1.6 1.45H8.15a1.6 1.6 0 0 1-1.6-1.45L5.5 8Z" />
      <path d="M8.75 10.5V7a3.25 3.25 0 0 1 6.5 0v3.5" />
    </>
  ),

  /* travel — aircraft on departure */
  travel: (
    <path d="M12 2.6a1.7 1.7 0 0 1 1.7 1.7v4.9l7 4.1v2.2l-7-2.1v4.4l2.3 1.7v1.8L12 20.5l-4 .8v-1.8l2.3-1.7v-4.4l-7 2.1v-2.2l7-4.1V4.3A1.7 1.7 0 0 1 12 2.6Z" />
  ),

  /* realestate — property */
  realestate: (
    <>
      <path d="M3.2 10.3 12 3.5l8.8 6.8" />
      <path d="M5.5 8.8V20a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5V8.8" />
      <path d="M9.75 21.5v-6.25h4.5v6.25" />
    </>
  ),

  /* media — playback on a screen */
  media: (
    <>
      <rect x="2.5" y="4" width="19" height="13" rx="2.5" />
      <path d="M9.8 8.6 15.4 11.5 9.8 14.4V8.6Z" />
      <path d="M12 17v4M8.5 21h7" />
    </>
  ),

  /* publicsector — civic building with a dome */
  publicsector: (
    <>
      <path d="M12 5.5V3.5" />
      <path d="M6.5 11a5.5 5.5 0 0 1 11 0" />
      <path d="M7.5 11v7M12 11v7M16.5 11v7" />
      <path d="M4.5 18h15" />
      <path d="M3 21h18" />
    </>
  ),

  /* insurance — cover held over you */
  insurance: (
    <>
      <path d="M21.5 12a9.5 9.5 0 0 0-19 0Z" />
      <path d="M12 12v6a2.75 2.75 0 0 0 5.5 0" />
    </>
  ),

  /* software — a terminal prompt */
  software: (
    <>
      <rect x="2.5" y="4" width="19" height="16" rx="2.5" />
      <path d="m7 10 2.75 2.75L7 15.5" />
      <path d="M13 15.5h4.5" />
    </>
  ),

  /* logistics — freight in transit */
  logistics: (
    <>
      <rect x="2.5" y="6.5" width="11" height="9" rx="1.5" />
      <path d="M13.5 10.5h3.7l4.3 3.9v1.1h-8" />
      <circle cx="7" cy="18.6" r="2.1" />
      <circle cx="17.2" cy="18.6" r="2.1" />
      <path d="M9.1 18.6h6" />
    </>
  ),

  /* ------------------------------------------------------------------ *
   * ui
   * ------------------------------------------------------------------ */

  "arrow-up-right": (
    <>
      <path d="M6 18 18 6" />
      <path d="M8.75 6H18v9.25" />
    </>
  ),

  "arrow-right": (
    <>
      <path d="M3.5 12h17" />
      <path d="m14 5.5 6.5 6.5-6.5 6.5" />
    </>
  ),

  "arrow-left": (
    <>
      <path d="M20.5 12h-17" />
      <path d="M10 5.5 3.5 12l6.5 6.5" />
    </>
  ),

  "chevron-down": <path d="m4.75 8.75 7.25 7.25 7.25-7.25" />,

  "chevron-right": <path d="m8.75 4.75 7.25 7.25-7.25 7.25" />,

  check: <path d="m4.5 12.5 5 5 10-11" />,

  close: (
    <>
      <path d="m5.5 5.5 13 13" />
      <path d="m18.5 5.5-13 13" />
    </>
  ),

  play: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M10.2 8.4 16 12l-5.8 3.6V8.4Z" />
    </>
  ),

  /* quote — an opening pull-quote pair, each mark drawn as one stroke */
  quote: (
    <>
      <path d="M9.5 14a2.5 2.5 0 1 1-5 0c0-3.6 1.7-6 5-7" />
      <path d="M19.5 14a2.5 2.5 0 1 1-5 0c0-3.6 1.7-6 5-7" />
    </>
  ),

  plus: <path d="M12 5v14M5 12h14" />,

  minus: <path d="M4.5 12h15" />,

  external: (
    <>
      <path d="M14 3.5h6.5V10" />
      <path d="M20.5 3.5 11.5 12.5" />
      <path d="M19 13.5v5a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
    </>
  ),

  clock: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 6.5V12l3.9 2.3" />
    </>
  ),

  users: (
    <>
      <circle cx="9.5" cy="8" r="3.6" />
      <path d="M2.5 20.5v-1A5.5 5.5 0 0 1 8 14h3a5.5 5.5 0 0 1 5.5 5.5v1" />
      <path d="M16.8 4.9a3.6 3.6 0 0 1 0 6.2" />
      <path d="M18 14.2a5.5 5.5 0 0 1 3.5 5.1v1.2" />
    </>
  ),

  code: (
    <>
      <path d="M9 7 4.5 12 9 17" />
      <path d="m15 7 4.5 5-4.5 5" />
      <path d="M13.6 4.8 10.4 19.2" />
    </>
  ),

  layers: (
    <>
      <path d="M12 3.2 3.4 7.7l8.6 4.5 8.6-4.5L12 3.2Z" />
      <path d="m3.4 12.4 8.6 4.5 8.6-4.5" />
      <path d="m3.4 17 8.6 4.5 8.6-4.5" />
    </>
  ),

  spark: (
    <path d="M12 3.5C12 8.5 15.5 12 20.5 12 15.5 12 12 15.5 12 20.5 12 15.5 8.5 12 3.5 12 8.5 12 12 8.5 12 3.5Z" />
  ),

  "shield-check": (
    <>
      <path d="M12 2.8 4.5 5.9v5.5c0 4.8 3.1 8.9 7.5 10.1 4.4-1.2 7.5-5.3 7.5-10.1V5.9L12 2.8Z" />
      <path d="m8.6 12.1 2.4 2.4 4.4-4.9" />
    </>
  ),

  mail: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="m3.3 6.8 7.6 5.2a2 2 0 0 0 2.2 0l7.6-5.2" />
    </>
  ),

  phone: (
    <path d="M9 3H6a2.5 2.5 0 0 0-2.5 2.6C3.5 13 10.9 20.5 18.3 20.5A2.5 2.5 0 0 0 20.9 18v-2.8l-4.7-1.6-2 2a14.6 14.6 0 0 1-4.7-4.7l2-2L9 3Z" />
  ),

  pin: (
    <>
      <path d="M12 21.5c4.7-4.6 7-8.4 7-11.5a7 7 0 1 0-14 0c0 3.1 2.3 6.9 7 11.5Z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),

  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10.2h18" />
      <path d="M8.2 3v4M15.8 3v4" />
    </>
  )
};

/* Every name this set ships, for reference / iteration. */
export const ICON_NAMES = Object.keys(GLYPHS);

export default function Icon({ name, size = 24, className, title, ...rest }) {
  const glyph = GLYPHS[name];
  // An unknown name renders nothing rather than crashing the page.
  if (!glyph) return null;

  // The icon system owns optical weight: a caller can never re-weight a glyph.
  const safeRest = { ...rest };
  delete safeRest.strokeWidth;
  delete safeRest["stroke-width"];
  delete safeRest.viewBox;

  const labelled = typeof title === "string" && title.length > 0;

  return (
    <svg
      {...BASE}
      className={className}
      width={size}
      height={size}
      role={labelled ? "img" : undefined}
      aria-hidden={labelled ? undefined : "true"}
      focusable="false"
      {...safeRest}
    >
      {labelled ? <title>{title}</title> : null}
      {glyph}
    </svg>
  );
}
